import { useCallback, useEffect, useRef } from 'react';

/**
 * @see https://www.jayfreestone.com/writing/react-portals-with-hooks/
 * @param id target element's id attribute
 * @returns {HTMLElement}
 */
export default function usePortal(id) {
  const rootElementRef = useRef(null);

  const getRootElem = useCallback(() => {
    if (!rootElementRef.current) {
      rootElementRef.current = document.createElement('div');
    }

    return rootElementRef.current;
  }, []);

  const createRootElement = useCallback((id) => {
    const rootContainer = document.createElement('div');
    rootContainer.setAttribute('id', id);

    return rootContainer;
  }, []);

  const addRootElement = useCallback((rootElement) => {
    document.body.insertBefore(
      rootElement,
      document.body.lastElementChild.nextElementSibling,
    );
  }, []);

  useEffect(() => {
    const existingParent = document.querySelector(`#${id}`);
    const parentElement = existingParent || createRootElement(id);

    if (!existingParent) {
      addRootElement(parentElement);
    }

    parentElement.appendChild(rootElementRef.current);

    return () => {
      rootElementRef.current.remove();

      if (!parentElement.childElementCount) {
        parentElement.remove();
      }
    };
  }, [id, addRootElement, createRootElement]);

  return getRootElem();
}
